import { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, LayoutDashboard, Users, Bot, Key, Activity,
  Settings, LogOut, ChevronLeft, ChevronRight, Bell,
  Search, Building2, FileText, Cpu, Sparkles, Receipt,
  Globe, BarChart3, AlertTriangle, Brain, Gauge, Presentation, CreditCard, ListChecks,
  KeyRound, ShieldCheck, Plug, Target, Scale, Compass, NotebookPen, Goal, Calculator, type LucideIcon,
} from 'lucide-react';
import { ActiveSupportBanner } from '@/components/support-access/ActiveSupportBanner';
import { IdleWarningModal } from '@/components/auth/IdleWarningModal';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';
import { cn } from '@/utils/cn';
import { useAuthStore } from '@/stores/authStore';
import { useFeatureFlags } from '@/hooks/useFeatureFlag';
import type { FeatureFlag } from '@/lib/featureFlags';
import { isPlatformRole } from '@/lib/permissions';

interface NavItem {
  label: string;
  icon: LucideIcon;
  path: string;
  badge?: number;
  /** If set, the item is hidden when this flag is false for the current user. */
  requires?: FeatureFlag;
}

// Platform Super Admin nav.
// Everything here operates on platform aggregates — no tenant content.
// If a route shows individual identity / audit / AI insight data, it belongs
// under /org/* instead.
const superAdminNav: NavItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
  { label: 'Tenants', icon: Building2, path: '/admin/tenants' },
  { label: 'Subscriptions', icon: CreditCard, path: '/admin/subscriptions' },
  { label: 'Waitlist', icon: ListChecks, path: '/admin/waitlist' },
  { label: 'Platform Users', icon: Users, path: '/admin/platform-users' },
  { label: 'Platform Roles', icon: ShieldCheck, path: '/admin/platform-roles' },
  { label: 'Connectors', icon: Plug, path: '/admin/connectors' },
  { label: 'Support Access', icon: KeyRound, path: '/admin/support-access' },
  { label: 'Billing', icon: Receipt, path: '/admin/billing' },
  { label: 'Platform Insights', icon: Sparkles, path: '/admin/platform-insights' },
  { label: 'Platform Audit', icon: FileText, path: '/admin/platform-audit' },
  { label: 'Founder Home', icon: Compass, path: '/admin/founder-home' },
  { label: 'Founder Collab', icon: NotebookPen, path: '/admin/founder-collab' },
  { label: 'Founder Tracker', icon: Goal, path: '/admin/founder-tracker' },
  { label: 'Financial Model', icon: Calculator, path: '/admin/financial-model' },
  { label: 'Pitch Deck', icon: Presentation, path: '/admin/pitch-deck' },
  { label: 'Strategy', icon: Target, path: '/admin/strategy' },
  { label: 'Advisor', icon: Compass, path: '/admin/advisor' },
  { label: 'Platform Settings', icon: Settings, path: '/admin/settings' },
];

// Founder role: leadership-only nav. Only the 4 workspace pages + home.
// No tenants, billing, audit, users, roles. Intentional hard isolation.
const founderNav: NavItem[] = [
  { label: 'Home',         icon: LayoutDashboard, path: '/admin/founder-home' },
  { label: 'Collab',       icon: NotebookPen,     path: '/admin/founder-collab' },
  { label: 'Tracker',      icon: Goal,            path: '/admin/founder-tracker' },
  { label: 'Financial',    icon: Calculator,      path: '/admin/financial-model' },
  { label: 'Strategy',     icon: Target,          path: '/admin/strategy' },
  { label: 'Pitch Deck',   icon: Presentation,    path: '/admin/pitch-deck' },
  { label: 'Advisor',    icon: Compass,         path: '/admin/advisor' },
];

const orgAdminNav: NavItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/org/dashboard' },
  { label: 'CISO Dashboard', icon: Shield, path: '/org/dashboard/ciso' },
  { label: 'Human Identities', icon: Users, path: '/org/identities/human' },
  { label: 'Service Accounts', icon: Key, path: '/org/identities/service' },
  { label: 'AI Agents', icon: Bot, path: '/org/identities/ai-agents' },
  { label: 'NHI Overview', icon: Cpu, path: '/org/identities/nhi' },
  { label: 'Providers', icon: Globe, path: '/org/providers', requires: 'multiProviderSync' },
  { label: 'Analytics', icon: BarChart3, path: '/org/analytics' },
  { label: 'AI Analysis', icon: Brain, path: '/org/ai-analysis', requires: 'aiAnalysis' },
  { label: 'Token Observability', icon: Gauge, path: '/org/token-observability', requires: 'tokenObservability' },
  { label: 'Threat Center', icon: AlertTriangle, path: '/org/threats' },
  { label: 'EU AI Act', icon: Scale, path: '/org/ai-act', requires: 'aiActModule' },
  { label: 'Activity Log', icon: Activity, path: '/org/activity' },
  { label: 'Members', icon: Users, path: '/org/settings/members' },
  { label: 'Roles', icon: ShieldCheck, path: '/org/settings/roles' },
  { label: 'Settings', icon: Settings, path: '/org/settings' },
];

export function DashboardLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  // Session idle-timeout enforcement. Forces a sign-out after 15 min of no
  // user activity (mouse / keyboard / touch / scroll). Warning modal shows
  // up at 14 min, auto-dismisses as soon as activity resumes.
  const idle = useIdleTimeout();

  // Pick the nav set based on role:
  //   founder           → founderNav (4 leadership-only items)
  //   other platform    → superAdminNav (full operational surface)
  //   tenant            → orgAdminNav (/org/* surface)
  const flags = useFeatureFlags();
  const baseNav = user?.role === 'founder'
    ? founderNav
    : isPlatformRole(user?.role) ? superAdminNav : orgAdminNav;
  const navItems = baseNav.filter((item) => !item.requires || flags[item.requires]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="flex h-screen bg-dark-950 overflow-hidden">
      {/* Sidebar */}
      <aside
        className={cn(
          'flex flex-col border-r border-dark-800 bg-dark-900/50 backdrop-blur-sm transition-all duration-300',
          collapsed ? 'w-16' : 'w-64'
        )}
      >
        {/* Sidebar header */}
        <div className="flex items-center h-16 px-3 border-b border-dark-800">
          <Link to="/" className="flex items-center gap-2 overflow-hidden">
            <Shield className="h-7 w-7 text-cyber-400 shrink-0" />
            <AnimatePresence>
              {!collapsed && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  className="text-base font-bold text-white whitespace-nowrap"
                >
                  Identity<span className="text-cyber-400">Central</span>
                </motion.span>
              )}
            </AnimatePresence>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all group relative',
                  isActive
                    ? 'bg-cyber-500/10 text-cyber-400 border border-cyber-500/20'
                    : 'text-dark-400 hover:text-white hover:bg-dark-800/50 border border-transparent'
                )}
                title={collapsed ? item.label : undefined}
              >
                <item.icon className={cn('h-5 w-5 shrink-0', isActive && 'text-cyber-400')} />
                <AnimatePresence>
                  {!collapsed && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="whitespace-nowrap"
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>
                {item.badge && !collapsed && (
                  <span className="ml-auto bg-threat-500/20 text-threat-400 text-xs font-bold px-1.5 py-0.5 rounded-full">
                    {item.badge}
                  </span>
                )}
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-cyber-400 rounded-r" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Collapse toggle + user */}
        <div className="border-t border-dark-800 p-2 space-y-2">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center p-2 text-dark-400 hover:text-white hover:bg-dark-800 rounded-lg transition-colors"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
          <div className={cn('flex items-center gap-2 px-2 py-2 rounded-lg', collapsed && 'justify-center')}>
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-cyber-400 to-primary-500 flex items-center justify-center text-xs font-bold text-dark-950 shrink-0">
              {user?.name?.split(' ').map(n => n[0]).join('') || '?'}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-dark-400 truncate">{user?.role?.replace('_', ' ')}</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-16 border-b border-dark-800 bg-dark-900/30 backdrop-blur-sm flex items-center justify-between px-6">
          <div className="flex items-center gap-3 flex-1 max-w-md">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-dark-500" />
              <input
                type="text"
                placeholder="Search identities, policies, logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-sm text-white placeholder-dark-500 focus:outline-none focus:border-cyber-500/50 focus:ring-1 focus:ring-cyber-500/20 transition-colors"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button className="relative p-2 text-dark-400 hover:text-white hover:bg-dark-800 rounded-lg transition-colors">
              <Bell className="h-5 w-5" />
              <span className="absolute top-1 right-1 h-2 w-2 bg-threat-500 rounded-full" />
            </button>
            <div className="w-px h-8 bg-dark-700 mx-1" />
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-sm text-dark-400 hover:text-threat-400 hover:bg-dark-800 rounded-lg transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </header>

        {/* Active support-access grant banner (shown only when applicable) */}
        <ActiveSupportBanner />

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-dark-950 bg-grid-pattern">
          <Outlet />
        </main>
      </div>

      <IdleWarningModal
        visible={idle.warningVisible}
        msUntilSignOut={idle.msUntilSignOut}
        onStayActive={idle.stayActive}
      />
    </div>
  );
}
